import React, {useEffect, useState} from "react"

import {Col, Row} from "react-bootstrap"
import Flex from "@irati/ui/src/Flex"
import {Body, Label} from "@irati/ui/src/Typography"
import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import RecipientList from "@irati-web/reward/src/components/Checkout/Steps/Recipients/RecipientList"
import Hr from "@irati/ui/src/Layout/Hr"
import Spacing from "@irati/ui/src/Spacing"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Button from "@irati/ui/src/Button"
import History from "@irati/utils/history"
import * as PropTypes from "prop-types"
import loadCompanyValues from "@irati/companyvalues/apollo/query/LoadCompanyValues"
import OccasionsService from "@irati/recognition/services/Occasions"
import {Occasion} from "@irati/types"
import authentication from "@irati/auth"
import {CompanyValue} from "@irati/companyvalues/types"
import DistributeRewardStepIndicator from "./DistributeRewardStepIndicator"
import {StepNames} from "../../../enums"
import {OccasionIcon, SelectedRecognitionValue} from "../styled"
import {DistributeRewardImportType, DistributeRewardRecipient} from "../../../enums/DistributeRewardTypes"
import {DistributeRewardState} from "../../../stores/DistributeReward/actions"

interface Props extends InjectIntl {
  payload: DistributeRewardState
}

const DistributeRewardStep3: React.FC<Props> = ({payload, formatMessage}, context) => {
  const [occasion, setOccasion] = useState<Occasion | null>(null)
  const [companyValues, setCompanyValues] = useState<CompanyValue[] | null>(null)

  useEffect(() => {
    if(payload.userEmails.length === payload.destinationUsers.length) {
      History.push(`/wallet/flexi-assign/${StepNames.Recipients}`)
    }

    OccasionsService.getAll().then((occasions) => {
      occasions.map((oc: Occasion) => {
        if (oc.uuid === payload.recognition.occasionId) {
          setOccasion(oc)
        }
      })
    })

    const getCompanyValues = async() => {
      const companies: CompanyValue[] = []

      const params = {
        limit: 9999,
        tenantId: authentication.business().uuid
      }

      const data = await loadCompanyValues(params)

      data.results.map((result: CompanyValue) => {
        if (payload.recognition.companyValues.includes(result?.uuid)) {
          companies.push(result)
        }
      })
      setCompanyValues(companies)
    }

    getCompanyValues()
  }, [])

  return <>
    <DistributeRewardStepIndicator currentStep={3} />
    <Flex direction="column">
      <Flex justifyContent="space-between" alignItems="center">
        <Spacing marginBottom>
          <Label large marginRight>
            <FormattedMessage id="wallet.reward.admin.distribute.recipients" />
          </Label>
          {payload.rewardImportType === DistributeRewardImportType.IMPORT_CSV &&
          payload.userEmails.length
          }
        </Spacing>
        <Button tertiary small onClick={() => History.push(`/flexi-assign/${StepNames.Recipients}`, context)}>
          <FormattedMessage id="platform.button.edit" />
        </Button>
      </Flex>
      {payload.rewardImportType === DistributeRewardImportType.MANUAL_ENTER && 
        <RecipientList recipients={payload.destinationUsers} />
      }
    </Flex>
    <Hr />
    <Flex direction="column">
      <Flex justifyContent="space-between" alignItems="center">
        <Label large marginBottom={2}>
          <FormattedMessage id="wallet.reward.admin.distribute.pointsPerUser" />
        </Label>
        <Button tertiary small onClick={() => History.push(`/flexi-assign/${StepNames.Message}`, context)}>
          <FormattedMessage id="platform.button.edit" />
        </Button>
      </Flex>
      <Flex alignItems="center">
        <Icon icon={IconTypes.rewardPoints} size={16} />
        <Body small marginLeft={0.5}>
          {payload.pointsPerUser}
        </Body>
      </Flex>
    </Flex>
    <Hr />
    <Flex direction="column">
      <Flex justifyContent="space-between" alignItems="center">
        <Label large marginBottom>
          <FormattedMessage id="wallet.reward.admin.distribute.message" />
        </Label>
        <Button tertiary small onClick={() => History.push(`/flexi-assign/${StepNames.Message}`, context)}>
          <FormattedMessage id="platform.button.edit" />
        </Button>
      </Flex>
      <Row>
        <Col md={12}>
          <Label>{payload.message}</Label>
        </Col>
      </Row>
    </Flex>

    {authentication.apps("recognition") &&
    payload.recipientType === DistributeRewardRecipient.EMPLOYEE &&
    payload.rewardImportType === DistributeRewardImportType.MANUAL_ENTER &&
    <>
      <Flex direction="column" marginTop={2}>
        <Flex justifyContent="space-between" alignItems="center">
          <Label large marginBottom>
            <FormattedMessage id="platform.fields.share.label" />
          </Label>
        </Flex>
        <Row>
          <Col md={12}>
            <Label>{payload.recognition?.share?.label}</Label>
          </Col>
        </Row>
      </Flex>

      {occasion && <Spacing marginTop={2}>
        <Row>
          <Col md={12}>
            <Label large display="block" marginBottom>
              <FormattedMessage id="platform.fields.occasions.select.label" />
            </Label>
          </Col>
          <Col md={12}>
            <SelectedRecognitionValue>
              <OccasionIcon src={formatMessage({id: occasion?.icon?.href})} alt={formatMessage({id: occasion?.icon?.alt})} />
              <Label>{formatMessage({id: occasion?.name})}</Label>
            </SelectedRecognitionValue>
          </Col>
        </Row>
      </Spacing>}

      {companyValues && companyValues.length !==0 && <Spacing marginTop={2}>
        <Row>
          <Col md={12}>
            <Label large display="block" marginBottom>
              <FormattedMessage id="platform.fields.companyValue.label" values={{name: authentication.userTenant().public_name}} />
            </Label>
          </Col>
          <Col md={12}>
            {companyValues?.map((values) => (
              <SelectedRecognitionValue key={values.uuid}>
                <OccasionIcon src={values?.icon.href} alt={values?.icon.alt} />
                <Label>{values?.tag}</Label>
              </SelectedRecognitionValue>
            ))}
          </Col>
        </Row>
      </Spacing>}
    </>
    }
  </>
}

DistributeRewardStep3.contextTypes = {
  appPath: PropTypes.string.isRequired
}

export default injectIntl(DistributeRewardStep3)
